import React, { useState, useMemo, useEffect, useRef } from "react";
import { FixedSizeList as List } from "react-window";
import GlobalButton from "../../../shared/utils/GlobalButton";
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  Table,
  TableHead,
  TextField,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  CircularProgress,
  Radio,
  Tooltip,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { PRODUCT_TYPES } from "../../../shared/enums/productTypeList";

const brandingOptions = ["Outdoor Branding", "Indoor Branding"];
const initialColumns = [
  { field: "select", headerName: "Select", type: "checkbox", width: 70 },
  { field: "category", headerName: "Category", type: "text", width: 150 },
  {
    field: "subCategory",
    headerName: "Sub Category",
    type: "text",
    width: 170,
  },
  { field: "question", headerName: "Question", type: "text", width: 230 },
  {
    field: "questionType",
    headerName: "Answer Type",
    type: "select",
    options: ["Text", "Yes/No", "Date", "Multiple Select"],
    width: 130,
  },
  {
    field: "mandatory",
    headerName: "Mandatory",
    type: "select",
    options: ["Yes", "No"],
    width: 120,
  },
  {
    field: "photograph",
    headerName: "Photograph",
    type: "select",
    options: ["Yes", "No"],
    width: 120,
  },
  {
    field: "multipleOptions",
    headerName: "Options",
    type: "dynamic",
    width: 240,
  },
  {
    field: "finalValue",
    headerName: "Final Value",
    type: "text",
    width: 240,
  },
  { field: "actions", headerName: "Actions", type: "actions", width: 130 },
];

// Tooltip TableCell Component
const TooltipTableCell = ({
  children,
  width,
  minWidth,
  maxWidth,
  ...props
}) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const isTextOverflowed =
        textRef.current.scrollWidth > textRef.current.clientWidth ||
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setIsOverflowed(isTextOverflowed);
    }
  }, [children]);

  return (
    <TableCell
      {...props}
      sx={{
        width: width,
        minWidth: minWidth,
        maxWidth: maxWidth,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        padding: "6px 8px",
        ...props.sx,
      }}
    >
      <Tooltip
        title={children}
        disableHoverListener={!isOverflowed}
        placement="top-start"
        arrow
      >
        <Box
          ref={textRef}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

export default function ProductCategoryQuestionUI() {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [description, setDescription] = useState("");
  const [brandingType, setBrandingType] = useState("");
  const [rows, setRows] = useState([]);
  const [editingRows, setEditingRows] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [productOptions, setProductOptions] = useState(PRODUCT_TYPES || []);
  const [errors, setErrors] = useState({
    product: false,
    brandingType: false,
    description: false,
    question: {},
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const serverURL = process.env.REACT_APP_URL;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const templateId = queryParams.get("templateId");
  const scrollRef = useRef(null);

  const fetchItemMasterProductList = async () => {
    try {
      const res = await fetch(`${serverURL}/itemMaster/all-product-type`, {
        headers: { Authorization: ` Bearer ${localStorage.getItem("token")}` },
      });
      const itemMasterData = await res.json();
      return itemMasterData?.products;

      // filter out any static product that appears in API
      // const filtered = PRODUCT_TYPES.filter((p) => !apiProducts.includes(p));

      // setProductOptions(filtered);
    } catch (error) {
      console.error("Error fetching item master product list:", error);
    }
  };

  // const fetchProductList = async () => {
  //   try {
  //     const res = await fetch(`${serverURL}/PDI/getProductList`, {
  //       headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  //     });
  //     const apiProducts = await res.json();

  //     const itemMasterProducts = await fetchItemMasterProductList();

  //     const combineProducts = [
  //       ...new Set([...(PRODUCT_TYPES || []), ...(itemMasterProducts || [])]),
  //     ];

  //     setProductOptions(
  //       combineProducts.filter((p) => !apiProducts.includes(p)),
  //     );

  //     // filter out any static product that appears in API
  //     // const filtered = PRODUCT_TYPES.filter((p) => !apiProducts.includes(p));

  //     // setProductOptions([...filtered, ...itemMasterProducts]);
  //   } catch (error) {
  //     console.error("Error fetching product list:", error);
  //   }
  // };

  // useEffect(() => {
  //   if (!templateId) {
  //     console.log("templete id is not here",templateId)
  //     fetchProductList();
  //   } else {
  //     console.log("temp")
  //     setProductOptions(PRODUCT_TYPES);
  //   }
  // }, [templateId]);

  const fetchProductList = async (isEditMode = false) => {
    try {
      const res = await fetch(`${serverURL}/PDI/getProductList`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const apiProducts = await res.json();

      const itemMasterProducts = await fetchItemMasterProductList();

      const newProducts =
        itemMasterProducts?.filter((p) => !apiProducts.includes(p)) || [];

      // ✅ CREATE MODE → only NEW
      if (!isEditMode) {
        setProductOptions(newProducts);
        return;
      }

      // ✅ EDIT MODE → OLD + NEW
      const combinedProducts = [
        ...new Set([...(PRODUCT_TYPES || []), ...newProducts]),
      ];

      setProductOptions(combinedProducts);
    } catch (error) {
      console.error("Error fetching product list:", error);
    }
  };
  useEffect(() => {
    if (!templateId) {
      fetchProductList(false); // create
    } else {
      fetchProductList(true); // edit
    }
  }, [templateId]);
  // Filter rows for current selected product
  const filteredRows = useMemo(
    () => rows.filter((row) => row.product === selectedProduct),
    [rows, selectedProduct],
  );

  const validateFields = () => {
    const newErrors = {
      product: !selectedProduct,
      brandingType: !brandingType,
      description: !description.trim(),
    };
    setErrors(newErrors);
    return (
      !newErrors.product && !newErrors.brandingType && !newErrors.description
    );
  };

  const validateQuestionField = (question, rowId) => {
    const isValid = question && question.trim() !== "";
    setErrors((prev) => ({
      ...prev,
      question: {
        ...prev.question,
        [rowId]: !isValid,
      },
    }));
    return isValid;
  };

  const CampaignRow = ({ index, style, data }) => {
    const campaign = data.campaigns[index];
    const isSelected = data.selectedCampaign?._id === campaign._id;

    return (
      <div style={style}>
        <Paper
          key={campaign._id}
          sx={{
            p: 2,
            m: 1,
            cursor: "pointer",
            border: isSelected
              ? "1px solid var(--primary-color)"
              : "1px solid gray",
            borderRadius: 2,
          }}
          onClick={() => data.setSelectedCampaign(campaign)}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Radio checked={isSelected} />

            {campaign.organizationId?.brandLogo && (
              <img
                src={campaign.organizationId.brandLogo}
                alt="Brand Logo"
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                }}
              />
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography fontWeight="600">
                {CapitalizeFirstLetter(campaign.campaignTitle)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {campaign._id?.slice(-6)}
              </Typography>
            </Box>

            <Box>{getProductTypesFromCampaign(campaign)}</Box>
          </Box>
        </Paper>
      </div>
    );
  };

  // Helper function to extract product types from campaign
  const getProductTypesFromCampaign = (campaign) => {
    if (
      campaign?.rfpId?.productType &&
      campaign?.rfpId?.productType.length > 0
    ) {
      const productTypes = campaign?.rfpId?.productType || [];

      if (productTypes.length === 1) {
        return (
          <Box
            sx={{
              textAlign: "right",
              minWidth: "120px",
              color: "var(--primary-color)",
            }}
          >
            {productTypes[0]}
          </Box>
        );
      } else {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              minWidth: "120px",
            }}
          >
            <Box sx={{ textAlign: "right", color: "var(--primary-color)" }}>
              {productTypes[0]}
            </Box>
            <Box
              sx={{ color: "var(--para-color)", fontSize: "0.8rem", mt: 0.5 }}
            >
              +{productTypes.length - 1} others
            </Box>
          </Box>
        );
      }
    }
    return null;
  };

  const filteredCampaigns = useMemo(() => {
    if (!campaignSearch.trim()) {
      return availableCampaigns;
    }
    const searchTerm = campaignSearch.toLowerCase();
    return availableCampaigns.filter((campaign) =>
      campaign.campaignTitle.toLowerCase().includes(searchTerm),
    );
  }, [availableCampaigns, campaignSearch]);

  // Handle individual question selection
  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(questionId);
      if (isAlreadySelected) {
        return prevSelected.filter((id) => id !== questionId);
      } else {
        return [...prevSelected, questionId];
      }
    });
  };

  // Handle select all questions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allQuestionIds = filteredRows.map((row) => row._id);
      setSelectedQuestions(allQuestionIds);
      setSelectAll(true);
    } else {
      setSelectedQuestions([]);
      setSelectAll(false);
    }
  };

  // Update selectAll state when selection changes
  useEffect(() => {
    if (
      filteredRows.length > 0 &&
      selectedQuestions.length === filteredRows.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedQuestions, filteredRows]);

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${serverURL}/PDI/fetchCampaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data?.data) {
        setAvailableCampaigns(data.data);
      } else {
        setAvailableCampaigns([]);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setAvailableCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Open assignment dialog
  const handleOpenAssignDialog = () => {
    if (selectedQuestions.length === 0) {
      alert("Please select at least one question to assign to a campaign.");
      return;
    }
    setCampaignSearch("");
    setSelectedCampaign(null);
    fetchCampaigns();
    setAssignDialogOpen(true);
  };

  // Close assignment dialog
  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedCampaign(null);
    setCampaignSearch("");
  };

  const handleAssignToCampaign = async () => {
    if (!selectedCampaign) {
      handleCloseAssignDialog();
      setTimeout(() => {
        Swal.fire({
          icon: "warning",
          title: "No Campaign Selected",
          text: "Please select a campaign to link the questions.",
          confirmButtonColor: "#3085d6",
        });
      }, 100);
      return;
    }

    setTimeout(async () => {
      const confirmationResult = await Swal.fire({
        icon: "question",
        title: "Confirm Link",
        text: `Are you sure you want to link selected questions to "${selectedCampaign.campaignTitle}"?`,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Link!",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) {
            swalContainer.style.zIndex = "20000";
          }
        },
      });

      if (!confirmationResult.isConfirmed) {
        return;
      }

      setAssignmentLoading(true);
      try {
        const token = localStorage.getItem("token");
        const selectedQuestionObjects = filteredRows.filter((row) =>
          selectedQuestions.includes(row._id),
        );

        const response = await fetch(`${serverURL}/PDI/createQuestionnaire`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaignName: selectedCampaign.campaignTitle,
            campaignId: selectedCampaign._id,
            questionIds: selectedQuestions,
            questions: selectedQuestionObjects,
            productType: selectedProduct,
          }),
        });

        const result = await response.json();

        if (response.ok) {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Questions successfully link to ${selectedCampaign.campaignTitle}`,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
            didOpen: () => {
              const swalContainer = document.querySelector(".swal2-container");
              if (swalContainer) {
                swalContainer.style.zIndex = "20000";
              }
            },
          });
          handleCloseAssignDialog();

          setSelectedQuestions([]);
        } else {
          throw new Error(
            result.error || "Failed to assign questions to campaign",
          );
        }
      } catch (error) {
        console.error("Error assigning questions to campaign:", error);
        await Swal.fire({
          icon: "error",
          title: "Not Assigned",
          text: error.message,
          confirmButtonColor: "#d33",
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container");
            if (swalContainer) {
              swalContainer.style.zIndex = "20000";
            }
          },
        });
      } finally {
        setAssignmentLoading(false);
      }
    }, 150);
  };

  const fetchQuestions = async (templateId, searchTerm = "", product = "") => {
    let baseUrl;

    if (templateId) {
      baseUrl = `${serverURL}/PDI/getPdiQuestions/${templateId}`;
    } else {
      baseUrl = `${serverURL}/PDI/getPdiQuestions`;
    }

    // Build query parameters
    const params = new URLSearchParams();

    if (searchTerm) {
      params.append("search", searchTerm);
    }

    if (product) {
      params.append("product", product);
    }

    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return res.json();
  };

  useEffect(() => {
    const loadData = async () => {
      if (!templateId) {
        setInitialLoading(false);
      }
      setLoading(true);
      try {
        const data = await fetchQuestions(
          templateId,
          searchTerm,
          selectedProduct,
        );
        console.log("✅ Data loaded with search and product:", data);

        // If we have a templateId OR we're searching with selectedProduct
        if (templateId || (searchTerm && selectedProduct)) {
          setRows(
            data.flatMap((product) =>
              product.questions.map((q) => ({
                _id: q._id,
                product: product.name,
                category: q.category,
                subCategory: q.subCategory,
                finalValue: q.finalValue,
                question: q.question,
                questionType: q.questionType,
                answer: q.answer,
                mandatory: q.mandatory,
                photograph: q.photograph,
                multipleOptions: q.multipleOptions,
              })),
            ),
          );
        }

        // Don't override brandingType and description from search results
        if (templateId && data[0]) {
          setBrandingType(data[0]?.brandingType || "");
          setDescription(data[0]?.description || "");
          if (!selectedProduct) {
            setSelectedProduct(data[0]?.name || "");
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      loadData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [templateId, searchTerm, selectedProduct]);

  const handleCellChange = (rowId, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._id === rowId) {
          if (field === "questionType") {
            // Naya question type set karo
            const updatedRow = { ...row, [field]: value };

            // Purane data ko clear karo based on new type
            if (value === "Multiple Select") {
              // Multiple Select: multipleOptions ko empty array karo, answer ko clear karo
              updatedRow.multipleOptions = [];
              updatedRow.answer = "";
            } else {
              // Text/Date/Yes/No: multipleOptions ko empty array karo
              updatedRow.multipleOptions = [];
              // Answer field ko rehne do ya initialize karo
              updatedRow.answer = updatedRow.answer || "";
            }

            return updatedRow;
          }

          return { ...row, [field]: value };
        }
        return row;
      }),
    );

    if (field === "question") {
      validateQuestionField(value, rowId);
    }
  };

  const renderAnswerInput = (row) => {
    const qType = row.questionType;

    if (qType === "Multiple Select") {
      return (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={Array.isArray(row.multipleOptions) ? row.multipleOptions : []}
          onChange={(event, newValue) => {
            handleCellChange(row._id, "multipleOptions", newValue);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              paddingRight: "24px",
              maxHeight: 96,
              overflowY: "auto",
              alignItems: "flex-start",

              /* ❌ remove blue border */
              "&.Mui-focused fieldset": {
                borderColor: "transparent",
              },
            },
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type & press Enter"
              size="small"
            />
          )}
        />
      );
    }

    // Text, Date, Yes/No ke liye regular answer field
    return (
      <TextField
        size="small"
        value={row.answer || ""}
        placeholder={qType === "Multiple Select" ? "" : "Disabled"}
        onChange={(e) => handleCellChange(row._id, "answer", e.target.value)}
        fullWidth
        disabled={qType !== "Multiple Select"}
        sx={{
          "& .MuiInputBase-root": {
            backgroundColor: qType !== "Multiple Select" ? "#f8f8f8" : "white", // off-white when disabled
            cursor: qType !== "Multiple Select" ? "not-allowed" : "text", // disable cursor
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "#888", // grey text color for disabled state
          },
        }}
      />
    );
  };

  const createQuestionAPI = async (productName, data) => {
    const res = await fetch(
      `${serverURL}/PDI/products/${encodeURIComponent(
        productName,
      )}/createQuestion`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(data),
      },
    );
    return res.json();
  };

  const updateQuestionAPI = async (product, qId, data) => {
    const res = await fetch(
      `${serverURL}/PDI/products/${encodeURIComponent(
        product,
      )}/updateQuestion/${qId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(data),
      },
    );
    return res.json();
  };

  const deleteQuestionAPI = async (product, qId) => {
    const res = await fetch(
      `${serverURL}/PDI/products/${encodeURIComponent(
        product,
      )}/deleteQuestion/${qId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      },
    );
    return res.ok;
  };

  const handleAddRow = (productName) => {
    if (!validateFields()) {
      return;
    }
    const tempRow = {
      _id: `temp-${Date.now()}`,
      product: productName,
      category: "",
      subCategory: "",
      finalValue: "",
      question: "",
      questionType: "Text",
      answer: "",
      mandatory: "No",
      photograph: "No",
      multipleOptions: [],
    };
    setRows((prev) => [...prev, tempRow]);
    setEditingRows((prev) => ({ ...prev, [tempRow._id]: true }));
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 0);
  };

  const handleSaveRow = async (row) => {
    if (!description || description.trim() === "") {
      setErrors((prev) => ({ ...prev, description: true }));
      return;
    }
    if (!row.question || row.question.trim() === "") {
      setSnackbar({
        open: true,
        message: "Question field is required",
        severity: "error",
      });

      setErrors((prev) => ({
        ...prev,
        question: { ...prev.question, [row._id]: true },
      }));
      return;
    }

    try {
      if (row._id.startsWith("temp-")) {
        const created = await createQuestionAPI(row.product, {
          description,
          brandingType,
          createdBy: localStorage.getItem("name"),
          category: row.category,
          subCategory: row.subCategory,
          finalValue: row.finalValue,
          question: row.question,
          questionType: row.questionType,
          multipleOptions:
            row.questionType === "Multiple Select"
              ? row.multipleOptions || []
              : [],

          mandatory: row.mandatory,
          photograph: row.photograph,
          answer: row.answer,
          createdByEmail: localStorage.getItem("email"),
        });

        const newQ = created.questions[created.questions.length - 1];

        setRows((prev) =>
          prev.map((r) =>
            r._id === row._id ? { ...newQ, product: row.product } : r,
          ),
        );

        setErrors((prev) => ({
          ...prev,
          question: { ...prev.question, [row._id]: false },
        }));

        setSnackbar({
          open: true,
          message: "Question created successfully!",
          severity: "success",
        });
      } else {
        await updateQuestionAPI(row.product, row._id, {
          description,
          brandingType,
          updatedBy: localStorage.getItem("name"),
          updatedByEmail: localStorage.getItem("email"),
          category: row.category,
          multipleOptions:
            row.questionType === "Multiple Select"
              ? row.multipleOptions || []
              : [],
          mandatory: row.mandatory,
          photograph: row.photograph,
          subCategory: row.subCategory,
          finalValue: row.finalValue,
          question: row.question,
          questionType: row.questionType,
          answer: row.answer,
        });

        setErrors((prev) => ({
          ...prev,
          question: { ...prev.question, [row._id]: false },
        }));

        setSnackbar({
          open: true,
          message: "Question updated successfully!",
          severity: "success",
        });
      }

      setEditingRows((prev) => ({ ...prev, [row._id]: false }));
    } catch (err) {
      console.error("Error saving row:", err);
      setSnackbar({
        open: true,
        message: "Error saving question. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteRow = async (row) => {
    if (!row._id.startsWith("temp-")) {
      await deleteQuestionAPI(row.product, row._id);
    }
    setRows((prev) => prev.filter((r) => r._id !== row._id));
    setSelectedQuestions((prev) => prev.filter((id) => id !== row._id));
  };

  const getColumnWidth = (field) => {
    const column = initialColumns.find((col) => col.field === field);
    return column ? column.width : "auto";
  };

  return (
    <Box sx={{ p: 3 }} className="h-[93vh] overflow-y-scroll">
      {initialLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            gap: 3,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="h6" color="text.secondary">
            Loading product data...
          </Typography>
        </Box>
      ) : (
        <>
          <div className="flex items-start gap-6 mb-6">
            {/* Product Dropdown */}
            <div className="flex flex-col" style={{ minHeight: "72px" }}>
              <label
                className={`text-sm font-medium mb-1 ${
                  errors.product ? "text-red-500" : "text-gray-700"
                }`}
              >
                Product <span className="text-red-500">*</span>
              </label>
              <Select
                displayEmpty
                size="small"
                value={selectedProduct}
                onChange={(e) => {
                  setSelectedProduct(e.target.value);
                  setErrors((prev) => ({ ...prev, product: false }));
                }}
                input={<OutlinedInput />}
                sx={{ width: 250, height: 40, borderRadius: 2 }}
                error={errors.product}
                renderValue={(selected) => selected || "Select Product"}
                disabled={!!templateId}
              >
                {productOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={selectedProduct === name}
                      onChange={() => {
                        setSelectedProduct(name);
                        setErrors((prev) => ({ ...prev, product: false }));
                      }}
                      value={name}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
              <div style={{ height: "20px", marginTop: "4px" }}>
                {errors.product && (
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--error-color)" }}
                  >
                    Please select a product
                  </Typography>
                )}
              </div>
            </div>

            {/* Branding Type Dropdown */}
            <div className="flex flex-col" style={{ minHeight: "72px" }}>
              <label
                className={`text-sm font-medium mb-1 ${
                  errors.brandingType ? "text-red-500" : "text-gray-700"
                }`}
              >
                Branding Type <span className="text-red-500">*</span>
              </label>
              <Select
                displayEmpty
                size="small"
                value={brandingType}
                onChange={(e) => {
                  setBrandingType(e.target.value);
                  setErrors((prev) => ({ ...prev, brandingType: false }));
                }}
                input={<OutlinedInput />}
                sx={{ width: 250, height: 40, borderRadius: 2 }}
                error={errors.brandingType}
                renderValue={(selected) => selected || "Select Branding Type"}
              >
                {brandingOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <div style={{ height: "20px", marginTop: "4px" }}>
                {errors.brandingType && (
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--error-color)" }}
                  >
                    Please select a branding type
                  </Typography>
                )}
              </div>
            </div>

            {/* Description Field */}
            <div className="flex flex-col" style={{ minHeight: "72px" }}>
              <label
                className={`text-sm font-medium mb-1 ${
                  errors.description ? "text-red-500" : "text-gray-700"
                }`}
              >
                Product Description <span className="text-red-500">*</span>
              </label>
              <TextField
                variant="outlined"
                size="small"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, description: false }));
                }}
                placeholder="Enter description"
                sx={{
                  width: 350,
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": { height: "40px" },
                }}
                error={errors.description}
              />
              <div style={{ height: "20px", marginTop: "4px" }}>
                {errors.description && (
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--error-color)" }}
                  >
                    Please enter a description
                  </Typography>
                )}
              </div>
            </div>
          </div>

          {selectedProduct && (
            <Paper key={selectedProduct} sx={{ p: 2, mb: 4 }}>
              <Box
                sx={{
                  overflowY: "auto",
                  overflowX: "auto",
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-sm font-semibold text-gray-800">
                    Selected Question ({selectedQuestions.length})
                  </h2>

                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                        style={{ width: 220 }}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 absolute left-2.5 top-2 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                        />
                      </svg>
                    </div>

                    <Button
                      variant="contained"
                      onClick={handleOpenAssignDialog}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        backgroundColor: selectedQuestions.length
                          ? "#2563eb"
                          : "rgba(0,0,0,0.2)",
                        "&:hover": {
                          backgroundColor: selectedQuestions.length
                            ? "#1d4ed8"
                            : "rgba(0,0,0,0.2)",
                        },
                      }}
                      disabled={selectedQuestions.length === 0}
                    >
                      Link To Campaign
                    </Button>
                  </div>
                </div>

                {/* TABLE WITH TOOLTIPS */}
                <TableContainer
                  ref={scrollRef}
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    height: "calc(100vh - 415px)",
                    overflowY: "auto",
                    overflowX: "auto",
                    width: "100%",
                  }}
                >
                  <Table
                    stickyHeader
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "100%",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {initialColumns.map((col) => (
                          <TooltipTableCell
                            key={col.field}
                            width={col.width}
                            minWidth={col.width}
                            maxWidth={col.width}
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "background.paper",
                            }}
                          >
                            {col.field === "select" ? (
                              <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAll}
                                indeterminate={
                                  selectedQuestions.length > 0 &&
                                  selectedQuestions.length < filteredRows.length
                                }
                                size="small"
                              />
                            ) : (
                              col.headerName
                            )}
                          </TooltipTableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell
                            colSpan={initialColumns.length}
                            align="center"
                            sx={{ py: 6 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <CircularProgress size={18} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Loading questions...
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : filteredRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={initialColumns.length}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm
                                ? "No questions found matching your search"
                                : "No questions found for this product"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRows.map((row) => (
                          <TableRow
                            key={row._id}
                            sx={{
                              backgroundColor: selectedQuestions.includes(
                                row._id,
                              )
                                ? "action.selected"
                                : "inherit",
                            }}
                          >
                            {initialColumns.map((col) => {
                              const isEditing = editingRows[row._id];
                              const columnWidth = getColumnWidth(col.field);

                              if (col.field === "select") {
                                return (
                                  <TableCell
                                    key="select"
                                    sx={{
                                      width: columnWidth,
                                      minWidth: columnWidth,
                                      maxWidth: columnWidth,
                                      padding: "6px 8px",
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedQuestions.includes(
                                        row._id,
                                      )}
                                      onChange={() =>
                                        handleSelectQuestion(row._id)
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                );
                              }

                              if (col.field === "actions") {
                                return (
                                  <TableCell
                                    key="actions"
                                    sx={{
                                      width: columnWidth,
                                      minWidth: columnWidth,
                                      maxWidth: columnWidth,
                                      padding: "6px 8px",
                                    }}
                                  >
                                    {isEditing ? (
                                      <Tooltip title="Save">
                                        <IconButton
                                          onClick={() => handleSaveRow(row)}
                                          size="small"
                                        >
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={() =>
                                            setEditingRows((p) => ({
                                              ...p,
                                              [row._id]: true,
                                            }))
                                          }
                                          size="small"
                                        >
                                          <ModeEditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Delete">
                                      <IconButton
                                        color="error"
                                        onClick={() => handleDeleteRow(row)}
                                        size="small"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                );
                              }

                              if (isEditing) {
                                if (col.field === "questionType") {
                                  return (
                                    <TableCell
                                      key={col.field}
                                      sx={{
                                        width: columnWidth,
                                        minWidth: columnWidth,
                                        maxWidth: columnWidth,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        padding: "6px 8px",
                                      }}
                                    >
                                      <Select
                                        size="small"
                                        value={row[col.field]}
                                        onChange={(e) =>
                                          handleCellChange(
                                            row._id,
                                            col.field,
                                            e.target.value,
                                          )
                                        }
                                        sx={{ width: "100%" }}
                                      >
                                        {col.options.map((opt) => (
                                          <MenuItem key={opt} value={opt}>
                                            {opt}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </TableCell>
                                  );
                                }

                                if (col.field === "question") {
                                  return (
                                    <TableCell
                                      key={col.field}
                                      sx={{
                                        width: columnWidth,
                                        minWidth: columnWidth,
                                        maxWidth: columnWidth,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        padding: "6px 8px",
                                      }}
                                    >
                                      <TextField
                                        size="small"
                                        fullWidth
                                        value={row[col.field] || ""}
                                        onChange={(e) =>
                                          handleCellChange(
                                            row._id,
                                            col.field,
                                            e.target.value,
                                          )
                                        }
                                        error={errors.question?.[row._id]}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&.Mui-error fieldset": {
                                              borderColor: "var(--error-color)",
                                            },
                                          },
                                        }}
                                        placeholder="Please Enter question *"
                                      />
                                    </TableCell>
                                  );
                                }

                                if (
                                  col.field === "mandatory" ||
                                  col.field === "photograph"
                                ) {
                                  return (
                                    <TableCell
                                      key={col.field}
                                      sx={{
                                        width: columnWidth,
                                        minWidth: columnWidth,
                                        maxWidth: columnWidth,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        padding: "6px 8px",
                                      }}
                                    >
                                      <Select
                                        size="small"
                                        value={row[col.field]}
                                        onChange={(e) =>
                                          handleCellChange(
                                            row._id,
                                            col.field,
                                            e.target.value,
                                          )
                                        }
                                        sx={{ width: "100%" }}
                                      >
                                        {col.options.map((opt) => (
                                          <MenuItem key={opt} value={opt}>
                                            {opt}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </TableCell>
                                  );
                                }

                                if (col.field === "multipleOptions") {
                                  return (
                                    <TableCell
                                      key={col.field}
                                      sx={{
                                        width: columnWidth,
                                        minWidth: columnWidth,
                                        maxWidth: columnWidth,
                                        padding: "6px 8px",
                                      }}
                                    >
                                      {renderAnswerInput(row)}
                                    </TableCell>
                                  );
                                }

                                return (
                                  <TableCell
                                    key={col.field}
                                    sx={{
                                      width: columnWidth,
                                      minWidth: columnWidth,
                                      maxWidth: columnWidth,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      padding: "6px 8px",
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={row[col.field] || ""}
                                      onChange={(e) =>
                                        handleCellChange(
                                          row._id,
                                          col.field,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                );
                              }

                              // Display mode with tooltips
                              const cellValue = Array.isArray(row[col.field])
                                ? row[col.field].join(", ")
                                : row[col.field];

                              return (
                                <TooltipTableCell
                                  key={col.field}
                                  width={columnWidth}
                                  minWidth={columnWidth}
                                  maxWidth={columnWidth}
                                >
                                  {cellValue}
                                </TooltipTableCell>
                              );
                            })}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <div className=" w-[200px] mt-[16px] ml-auto">
                <GlobalButton
                  variant="primary"
                  size="small"
                  leftIcon={<AddIcon />}
                  onClick={() => handleAddRow(selectedProduct)}
                >
                  {`Add Question`}
                </GlobalButton>
              </div>
            </Paper>
          )}
        </>
      )}

      {/* Assignment Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Link Checklist To A Campaign
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: "400px", mx: "auto", textAlign: "center" }}
          >
            You've created a new PDI checklist! Link these to a campaign so that
            it can be used during PDI
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            height: "400px",
            mt: 2,
          }}
        >
          <Box sx={{ mb: 2, flexShrink: 0 }}>
            <TextField
              fullWidth
              placeholder="Search campaigns by name..."
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              size="medium"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused": {
                    border: "1px solid var(--primary-color)",
                    borderRadius: 2,
                    boxShadow: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            {campaignsLoading ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading campaigns...
                </Typography>
              </Box>
            ) : filteredCampaigns.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {campaignSearch
                    ? `No campaigns found`
                    : "No campaigns available"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <List
                  height={300}
                  itemCount={filteredCampaigns.length}
                  itemSize={100}
                  width="100%"
                  itemData={{
                    campaigns: filteredCampaigns,
                    selectedCampaign,
                    setSelectedCampaign,
                  }}
                >
                  {CampaignRow}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, gap: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={handleCloseAssignDialog}
            disabled={assignmentLoading}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignToCampaign}
            variant="contained"
            disabled={!selectedCampaign || assignmentLoading}
            sx={{ minWidth: 100, borderRadius: 2, fontWeight: "600" }}
          >
            {"Link"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
